import { useRef, useState } from "react";
import { Button, Center, Group, Loader, TextInput } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconRefresh } from "@tabler/icons-react";

interface OtpInputProps {
  onComplete: (code: string) => void;
  loading?: boolean;
  error?: string | null;
}

export function OtpInput({
  onComplete,
  loading = false,
  error,
}: OtpInputProps) {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);
    // Any edit after a submission re-enables auto-submit for the next complete entry.
    // We read isSubmitted before setState so the first edit after submission won't
    // immediately re-fire — only a freshly completed entry will.
    const wasSubmitted = isSubmitted;
    setIsSubmitted(false);

    if (digit && index < 5) {
      inputs.current[index + 1]?.focus();
    }

    if (digit && index === 5 && !wasSubmitted) {
      const code = newDigits.join("");
      if (code.length === 6) {
        setIsSubmitted(true);
        onComplete(code);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const paste = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (paste.length === 6) {
      const newDigits = paste.split("");
      setDigits(newDigits);
      inputs.current[5]?.focus();
      setIsSubmitted(true);
      onComplete(paste);
    }
  };

  const isSmallPhone = useMediaQuery("(max-width: 420px)");
  const boxWidth = isSmallPhone ? 42 : 52;
  const boxHeight = isSmallPhone ? 50 : 60;
  const boxFontSize = isSmallPhone ? 20 : 26;

  const hasError = !!error;
  const allFilled = digits.every((d) => d !== "");

  return (
    <div>
      <Group gap={isSmallPhone ? 6 : 10} justify="center">
        {digits.map((digit, i) => (
          <TextInput
            key={i}
            ref={(el) => {
              inputs.current[i] = el;
            }}
            value={digit}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={i === 0 ? handlePaste : undefined}
            maxLength={1}
            inputMode="numeric"
            autoComplete="one-time-code"
            styles={{
              input: {
                width: boxWidth,
                height: boxHeight,
                textAlign: "center",
                fontSize: boxFontSize,
                fontFamily: "'IBM Plex Mono', monospace",
                fontWeight: 500,
                color: hasError
                  ? "#dc2626"
                  : allFilled && !hasError
                  ? "#059669"
                  : "#04119A",
                background: hasError
                  ? "rgba(220,38,38,0.04)"
                  : allFilled && !hasError
                  ? "rgba(16,185,129,0.06)"
                  : digit
                  ? "rgba(4,17,154,0.04)"
                  : "#fff",
                border: "2px solid",
                borderColor: hasError
                  ? "#dc2626"
                  : allFilled && !hasError
                  ? "#10b981"
                  : digit
                  ? "#04119A"
                  : "#d1d5db",
                borderRadius: 8,
                padding: 0,
                transition: "all 150ms ease",
                boxShadow: "none",
              },
              wrapper: { width: boxWidth },
            }}
          />
        ))}
      </Group>

      {loading && (
        <Center mt="xs">
          <Loader size="xs" color="gcpBlue" />
        </Center>
      )}

      {hasError && allFilled && (
        <Center mt="sm">
          <Button
            size="xs"
            variant="light"
            color="red"
            leftSection={<IconRefresh size={13} />}
            loading={loading}
            onClick={() => {
              setIsSubmitted(true);
              onComplete(digits.join(""));
            }}
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
          >
            Try again
          </Button>
        </Center>
      )}
    </div>
  );
}

export default OtpInput;
